<template v-if="collaborationCounterOfferEnabled && isReady">
  <iModal
    title="Propose Counter Offer"
    :close-on-backdrop="false"
    :close-on-escape="false"
    :close-on-secondary="false"
    :max-width="750"
    @close="closeModal"
  >
    <template #body>
      <iColumn gap="large">
        <iColumn gap="none">
          <iSubHeading>Update Contract Terms</iSubHeading>
          <iText variant="subtle">
            Modify the contract terms below to propose a counter offer.
          </iText>
        </iColumn>

        <ContractProposalForm
          :form-data="formData"
          :validation-errors="validationErrors"
          :show-collaborator-name="false"
          @update:form-data="updateFormData"
        />
      </iColumn>
    </template>

    <template #footer>
      <iRow align="right" gap="standard" width="fill">
        <iColumn width="hug">
          <iButton variant="secondary" width="fill" @click="closeModal">
            Cancel
          </iButton>
        </iColumn>
        <iColumn width="hug">
          <iButton
            variant="primary"
            width="fill"
            :is-loading="isSubmittingCounterOffer"
            loading-spinner-position="inside"
            @click="submitCounter"
          >
            Submit Counter Offer
          </iButton>
        </iColumn>
      </iRow>
    </template>
  </iModal>
</template>

<script>
import { useFeatureStore } from "%/stores/feature-store";
import { mapState, mapActions } from "pinia";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import ContractProposalForm from "@/components/publication/ContractProposalForm.vue";

export default {
  name: "CounterOfferModal",
  components: {
    ContractProposalForm,
  },
  props: {
    proposal: {
      type: Object,
      default: null,
    },
  },
  emits: ["close", "submit", "cancel"],
  data() {
    return {
      formData: {
        inviteeName: "",
        scopeOfWork: "",
        flatFee: false,
        flatFeeAmount: "",
        flatFeeFrequency: null,
        percentRevenue: false,
        percentRevenueValue: "",
        performanceBonus: false,
        performanceBonusAmount: "",
        performanceBonusFrequency: "per_deliverable",
        noCompensation: false,
      },
      validationErrors: {},
      isReady: false,
    };
  },
  computed: {
    ...mapState(useCollaboratorStore, [
      "selectedProposalReview",
      "isSubmittingCounterOffer",
    ]),
    collaborationCounterOfferEnabled() {
      return useFeatureStore().isFeatureEnabled("collaborationCounterOffer");
    },
  },
  watch: {
    proposal: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initializeFormData();
          this.isReady = true;
        }
      },
    },
    selectedProposalReview: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initializeFormData();
          this.isReady = true;
        }
      },
    },
  },
  created() {
    if (this.proposal || this.selectedProposalReview) {
      this.initializeFormData();
      this.isReady = true;
    }
  },
  methods: {
    ...mapActions(useCollaboratorStore, ["updateInviteFormData"]),
    initializeFormData() {
      const proposal = this.proposal || this.selectedProposalReview;

      if (proposal && proposal.contractVersion) {
        const contractVersion = proposal.contractVersion;
        const frequency = contractVersion.frequency || null;

        this.formData = {
          inviteeName: proposal.owningOrgName || "",
          scopeOfWork: contractVersion.scope_of_work || "",

          flatFee: !!contractVersion.flat_fee,
          flatFeeAmount: contractVersion.flat_fee
            ? contractVersion.flat_fee.toString()
            : "",
          flatFeeFrequency: frequency
            ? this.getFrequencyObject(frequency)
            : null,

          percentRevenue: !!contractVersion.percentage_revenue,
          percentRevenueValue: contractVersion.percentage_revenue
            ? contractVersion.percentage_revenue.toString()
            : "",

          performanceBonus: !!contractVersion.performance_based_revenue,
          performanceBonusAmount: contractVersion.performance_based_revenue
            ? contractVersion.performance_based_revenue.toString()
            : "",
          performanceBonusFrequency: frequency
            ? this.getFrequencyObject(frequency)
            : null,
        };
      }
    },
    getFrequencyObject(frequencyValue) {
      const frequencyMap = {
        per_month: { name: "Per Month", value: "per_month" },
        per_week: { name: "Per Week", value: "per_week" },
        per_deliverable: { name: "Per Deliverable", value: "per_deliverable" },
        per_story: { name: "Per Story", value: "per_story" },
      };

      return frequencyMap[frequencyValue] || null;
    },
    updateFormData(newData) {
      this.formData = { ...newData };
    },
    closeModal() {
      this.$emit("cancel");
    },
    submitCounter() {
      this.validationErrors = this.validateForm(this.formData);

      if (Object.keys(this.validationErrors).length === 0) {
        this.$emit("submit", this.formData);
      }
    },
    validateForm(formData) {
      const errors = {};

      if (!formData.scopeOfWork) {
        errors.scopeOfWork = "Scope of work is required";
      }
      if (!formData.flatFee && !formData.percentRevenue && !formData.performanceBonus && !formData.noCompensation) {
        errors.performanceBonusDetails =
            "At least one compensation type is required";
      }

      if (formData.flatFee) {
        if (!formData.flatFeeAmount || formData.flatFeeAmount <= 0) {
          errors.flatFeeDetails =
            "Amount and frequency are required for Flat Fee";
        }
      }

      if (formData.percentRevenue) {
        if (!formData.percentRevenueValue) {
          errors.percentRevenueValue =
            "Percentage value is required for Percent of Revenue";
        } else if (parseFloat(formData.percentRevenueValue) <= 0) {
          errors.percentRevenueValue = "Percentage must be a positive number";
        }
      }

      if (formData.performanceBonus) {
        if (
          !formData.performanceBonusAmount ||
          !formData.performanceBonusFrequency ||
          !formData.performanceBonusFrequency.value
        ) {
          errors.performanceBonusDetails =
            "Amount and frequency are required for Performance Bonus";
        }
      }

      return errors;
    },
  },
};
</script>
